import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Paper, Typography, Grid, IconButton, Dialog, DialogContent, DialogTitle, Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import { useLazyGetGraphDataQuery } from 'api/homeApi';
import { useSelector } from 'react-redux';
import { formatDateTimeForDBQuery } from 'utils/AppUtil';
import Theme from 'theme/Theme';
import { Stack } from '@mui/system';


const formatYAxisValue = (value) => {
    if (value == null || value === '') return '';

    const numValue = Number(value);
    if (numValue >= 1000) {
        return `${(numValue / 1000).toFixed(1)}k`;
    }
    return numValue.toLocaleString(); // Handles decimals and commas for <1000
};


const formatYAxisLineValue = (value) => {
    if (value == null || value === '') return '';

    const numValue = Number(value);
    const bytes = numValue;

    // Always show in GBs
    const gbs = (bytes / (1024 ** 3)).toFixed(1);

    return `${gbs}`;
};

const gbValueShow = (value) => {
    if (value == null || value === '') return '';
    return `${value} GB`;
};


const BarGraph = ({ universityName, selectedDates: selectedDatesProp }) => {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [expandedChart, setExpandedChart] = useState(null);
    const [hiddenBarSeries, setHiddenBarSeries] = useState(new Set()); // Track hidden bar series (multi-toggle)
    const [hiddenLineSeries, setHiddenLineSeries] = useState(new Set()); // Track hidden line series (multi-toggle)
    const [hiddenCombinedSeries, setHiddenCombinedSeries] = useState(new Set()); // Track hidden combined series (multi-toggle)

    const { selectedDates: storedSelectedDates } = useSelector((state) => state.home);
    const selectedDates = selectedDatesProp ?? storedSelectedDates;

    const selectedSite = { syracyse: 0, caesars: 1, jmahq: 2 }[universityName] ?? 0;
    const site = { syracyse: 'su', caesars: 'caesars', jmahq: '5g' }[universityName] ?? 'su';
    const dbDates = useMemo(
        () => formatDateTimeForDBQuery(selectedDates, selectedSite),
        [selectedDates, selectedSite]
    );
    const [getGraphData, { data: getBarData }] = useLazyGetGraphDataQuery()

    useEffect(() => {
        getGraphData({ site: site, dbDates: dbDates })
    }, [site, dbDates, getGraphData])


    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                const parentWidth = containerRef.current.parentElement?.offsetWidth || containerRef.current.offsetWidth;
                setContainerWidth(parentWidth);
            }
        };

        updateWidth();
        const resizeObserver = new ResizeObserver(updateWidth);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const getSiteDay = (item) => site === "caesars" ? item.PST_Day : (item.EST_Day ?? item.PST_Day);
    const dateData = (getBarData || []).map(item => {
        const raw = getSiteDay(item);
        return typeof raw === 'string' ? raw : String(raw ?? '');
    });

    console.log('getBarData: ', getBarData);

    console.log('dateData: ', dateData);
    const tmoRx = getBarData?.map(item => formatYAxisLineValue(item.TMO_RX)) || [];
    const tmoTx = getBarData?.map(item => formatYAxisLineValue(item.TMO_TX)) || [];
    const attRx = getBarData?.map(item => formatYAxisLineValue(item.ATT_RX)) || [];
    const attTx = getBarData?.map(item => formatYAxisLineValue(item.ATT_TX)) || [];
    const private_RX = getBarData?.map(item => formatYAxisLineValue(item.Private_RX)) || [];
    const private_TX = getBarData?.map(item => formatYAxisLineValue(item.Private_TX)) || [];

    const getDateLabel = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const valueFormatter = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const combinedDataset = getBarData?.map(item => {
        const date = new Date(getSiteDay(item));
        return {
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            Users: ((item.TMO_Users || 0) + (item.ATT_Users || 0) + (item.Private_Users || 0)),
            VoLTE: ((item.TMO_VoLTE || 0) + (item.ATT_VoLTE || 0) + (item.Private_VoLTE || 0)),
            TX: ((item.TMO_TX || 0) + (item.ATT_TX || 0) + (item.Private_TX || 0)),
            RX: ((item.TMO_RX || 0) + (item.ATT_RX || 0) + (item.Private_RX || 0))
        };
    }) || [];

    const stackedGroupedDataset = getBarData?.map(item => ({
        date: getDateLabel(getSiteDay(item)),
        tmoVoLTE: item.TMO_VoLTE,
        tmoUsers: item.TMO_Users,
        attVoLTE: item.ATT_VoLTE,
        attUsers: item.ATT_Users,
        privateVoLTE: item.Private_VoLTE,
        privateUsers: item.Private_Users
    })) || [];

    // const combinedSeries = [
    //     { type: 'bar', dataKey: 'Users', label: 'Users (Sum)', color: Theme.palette.chartGrey, stack: 'bars' },
    //     { type: 'bar', dataKey: 'VoLTE', label: 'VoLTE (Sum)', color: Theme.palette.chartRed, stack: 'bars' },
    //     { type: 'line', dataKey: 'TX', label: 'TX (Sum)', color: Theme.palette.chartBlack, yAxisId: 'rightAxis' },
    //     { type: 'line', dataKey: 'RX', label: 'RX (Sum)', color: Theme.palette.chartBlue, yAxisId: 'rightAxis' },
    // ];

    const combinedSeries = [
        { type: 'bar', dataKey: 'Users', label: 'Users (Sum)', color: Theme.palette.chartGrey, stack: 'bars', yAxisId: 'leftAxis' },
        { type: 'bar', dataKey: 'VoLTE', label: 'VoLTE (Sum)', color: Theme.palette.chartRed, stack: 'bars', yAxisId: 'leftAxis' },
        { type: 'line', dataKey: 'TX', label: 'TX (Sum)', color: Theme.palette.chartBlack, yAxisId: 'rightAxis' },
        { type: 'line', dataKey: 'RX', label: 'RX (Sum)', color: Theme.palette.chartBlue, yAxisId: 'rightAxis' },
    ];


    const getChartWidth = (isExpanded = false) => {
        if (isExpanded) {
            return window.innerWidth - 100;
        }
        if (!containerWidth) return 300;
        return containerWidth - 43;
    };

    const handleExpandChart = (chartName) => {
        setExpandedChart(chartName);
    };

    const handleCloseExpanded = () => {
        setExpandedChart(null);
    };

    // Toggle bar series visibility
    const handleBarSeriesClick = (dataKey) => {
        setHiddenBarSeries(prev => {
            const next = new Set(prev);
            next.has(dataKey) ? next.delete(dataKey) : next.add(dataKey);
            return next;
        });
    };

    // Toggle line series visibility
    const handleLineSeriesClick = (seriesLabel) => {
        setHiddenLineSeries(prev => {
            const next = new Set(prev);
            next.has(seriesLabel) ? next.delete(seriesLabel) : next.add(seriesLabel);
            return next;
        });
    };

    // Toggle combined series visibility
    const handleCombinedSeriesClick = (seriesLabel) => {
        setHiddenCombinedSeries(prev => {
            const next = new Set(prev);
            next.has(seriesLabel) ? next.delete(seriesLabel) : next.add(seriesLabel);
            return next;
        });
    };

    // Filter line series based on hidden state
    const getFilteredLineSeries = () => {
        const allSeries = [
            { data: tmoRx, label: 'TMO RX', color: Theme.palette.chartTMobile, curve: 'linear' },
            { data: tmoTx, label: 'TMO TX', color: Theme.palette.chartTMobileLight, curve: 'linear' },
            { data: attRx, label: 'ATT RX', color: Theme.palette.chartATNT, curve: 'linear' },
            { data: attTx, label: 'ATT TX', color: Theme.palette.chartATNTLight, curve: 'linear' },
            { data: private_RX, label: 'Private RX', color: Theme.palette.chartPrivate, curve: 'linear' },
            { data: private_TX, label: 'Private TX', color: Theme.palette.chartPrivateLight, curve: 'linear' },
        ];
        return allSeries.filter(series => !hiddenLineSeries.has(series.label));
    };

    // Filter combined series based on hidden state
    const getFilteredCombinedSeries = () => {
        return combinedSeries.filter(series => !hiddenCombinedSeries.has(series.label));
    };

    const renderExpandButton = (chartName) => (
        <IconButton
            onClick={() => handleExpandChart(chartName)}
            sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                // zIndex: 1,
                color: "#005AA6",
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                }
            }}
        >
            <OpenInFullIcon />
        </IconButton>
    );

    // Define all bar series (User Counts By Carrier)
    const allBarSeries = [
        { dataKey: 'tmoVoLTE', label: 'TMO VoLTE', color: Theme.palette.chartTMobileLight, stack: 'tmo' },
        { dataKey: 'tmoUsers', label: 'TMO Users', color: Theme.palette.chartTMobile, stack: 'tmo' },
        { dataKey: 'attVoLTE', label: 'ATT VoLTE', color: Theme.palette.chartATNTLight, stack: 'att' },
        { dataKey: 'attUsers', label: 'ATT Users', color: Theme.palette.chartATNT, stack: 'att' },
        { dataKey: 'privateVoLTE', label: 'Private VoLTE', color: Theme.palette.chartPrivateLight, stack: 'private' },
        { dataKey: 'privateUsers', label: 'Private Users', color: Theme.palette.chartPrivate, stack: 'private' }
    ];

    // Filter bar series based on hidden state
    const filteredBarSeries = allBarSeries.filter(series => !hiddenBarSeries.has(series.dataKey));

    // Custom legend component for line chart
    const LineChartLegend = () => {
        const allSeries = [
            { label: 'TMO RX', color: Theme.palette.chartTMobile },
            { label: 'TMO TX', color: Theme.palette.chartTMobileLight },
            { label: 'ATT RX', color: Theme.palette.chartATNT },
            { label: 'ATT TX', color: Theme.palette.chartATNTLight },
            { label: 'Private RX', color: Theme.palette.chartPrivate },
            { label: 'Private TX', color: Theme.palette.chartPrivateLight },
        ];

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '12px 0px',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                }}
            >
                {allSeries.map((item) => {
                    const isHidden = hiddenLineSeries.has(item.label);
                    return (
                        <Box
                            key={item.label}
                            onClick={() => handleLineSeriesClick(item.label)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8,
                                cursor: 'pointer',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease',
                                opacity: isHidden ? 0.4 : 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 12,
                                    height: 12,
                                    backgroundColor: isHidden ? '#ccc' : item.color,
                                    borderRadius: '2px',
                                }}
                            />
                            <Box
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: isHidden ? '#999' : '#424242',
                                    textDecoration: isHidden ? 'line-through' : 'none',
                                }}
                            >
                                {item.label}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // Custom legend component for combined chart
    const CombinedChartLegend = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '12px 0px',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                }}
            >
                {combinedSeries.map((item) => {
                    const isHidden = hiddenCombinedSeries.has(item.label);
                    return (
                        <Box
                            key={item.label}
                            onClick={() => handleCombinedSeriesClick(item.label)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8,
                                cursor: 'pointer',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease',
                                opacity: isHidden ? 0.4 : 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 12,
                                    height: 12,
                                    backgroundColor: isHidden ? '#ccc' : item.color,
                                    borderRadius: '2px',
                                }}
                            />
                            <Box
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: isHidden ? '#999' : '#424242',
                                    textDecoration: isHidden ? 'line-through' : 'none',
                                }}
                            >
                                {item.label}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // Custom legend component for bar chart
    const BarChartLegend = () => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '12px 0px',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2,
            }}
        >
            {allBarSeries.map((item) => {
                const isHidden = hiddenBarSeries.has(item.dataKey);
                return (
                    <Box
                        key={item.dataKey}
                        onClick={() => handleBarSeriesClick(item.dataKey)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.8,
                            cursor: 'pointer',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            opacity: isHidden ? 0.4 : 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                backgroundColor: isHidden ? '#ccc' : item.color,
                                borderRadius: '2px',
                            }}
                        />
                        <Box
                            sx={{
                                fontSize: '13px',
                                fontWeight: 500,
                                color: isHidden ? '#999' : '#424242',
                                textDecoration: isHidden ? 'line-through' : 'none',
                            }}
                        >
                            {item.label}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );

    return (
        <>
            <Paper
                sx={{

                    // p: { xs: 1, sm: 2, md: 2 },
                    // pt: { xs: 2, sm: 2, md: 2 },
                    // pl: { xs: 1, sm: 2, md: 2 },
                    width: '100%',
                    border: "none",
                    boxShadow: "none",
                    overflow: 'hidden',
                }}
            >
                {/* <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}> */}
                {/* Stacked BarChart */}
                <Box sx={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: "10px", padding: "20px", backgroundColor: "#ffffff", position: 'relative' }}
                    mt={3}
                // boxShadow={3}
                >
                    <Grid
                        item xs={12}

                    >
                        {renderExpandButton('barChart')}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#0A2240" }}>User Counts By Carrier</Typography>
                            <BarChartLegend />
                            <Grid sx={{ width: '100%', overflow: 'hidden' }}>
                                <BarChart
                                    xAxis={[{
                                        data: dateData,
                                        dataKey: 'date',
                                        scaleType: 'band',
                                        tickSize: 10,
                                        height: 32,
                                        valueFormatter,
                                        tickLabelStyle: {
                                            fontWeight: 600,
                                        }
                                    }]}
                                    yAxis={[{
                                        valueFormatter: formatYAxisValue,
                                        tickLabelStyle: {
                                            fontWeight: 600,
                                        }
                                    }]}
                                    series={filteredBarSeries}
                                    dataset={stackedGroupedDataset}
                                    sx={{ padding: "0px" }}
                                    height={350}
                                    slots={{ legend: () => null }}
                                    slotProps={{
                                        axis: {
                                            x: { tickLabelStyle: { fill: 'black', fontWeight: 600 } },
                                            y: { tickLabelStyle: { fill: 'black', fontWeight: 600 } }
                                        }
                                    }}

                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>

                {/* LineChart */}
                <Box sx={{
                    border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: "10px", padding: "20px",
                    backgroundColor: "#ffffff",
                    //  boxShadow: 3,
                    position: 'relative'
                }} mt={3}>
                    <Grid item xs={12}

                    >
                        {renderExpandButton('lineChart')}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#0A2240" }}>Data Usage By Carrier</Typography>
                            <LineChartLegend />
                            <Grid sx={{ width: '100%', overflow: 'hidden' }}>
                                <LineChart
                                    xAxis={[{
                                        dataKey: 'date',
                                        data: dateData,
                                        scaleType: 'band',
                                        tickSize: 8,
                                        height: 32,
                                        valueFormatter,
                                        tickLabelStyle: {
                                            fontWeight: 600,
                                        }
                                    }]}

                                    yAxis={[{
                                        id: 'left',
                                        valueFormatter: gbValueShow,
                                        width: 80,
                                        fontWeight: 600,
                                        color: "#000",
                                        tickLabelStyle: {
                                            fontWeight: 600,
                                        }

                                    }]}
                                    series={getFilteredLineSeries()}
                                    // width={900}
                                    height={350}
                                    slots={{ legend: () => null }}
                                />

                            </Grid>
                        </Grid>
                    </Grid>
                </Box>

                {/* Combined Bar and Line Chart */}
                <Box
                    sx={{
                        border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: "10px",
                        padding: "20px",
                        backgroundColor: "#ffffff",
                        // boxShadow: '0px 8px 16px -8px rgba(0, 0, 0, 0.25)',  // ✅ FIXED: Bottom border shadow only
                        position: 'relative',
                        // borderBottom: '3px solid rgba(0, 0, 0, 0.25)',     // ✅ ADDED: Colored bottom border
                    }}
                    mt={3}
                >



                    <Grid item xs={12}>

                        {renderExpandButton('combinedChart')}
                        <Grid item xs={12} md={6} >
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#0A2240" }}>Total User Counts and Data Usage</Typography>
                            <Grid sx={{ width: '100%', overflow: 'hidden' }}>
                                <Stack fullWidth width="100%">
                                    <CombinedChartLegend />
                                    <ChartContainer
                                        margin={20}
                                        series={getFilteredCombinedSeries()}
                                        xAxis={[{
                                            dataKey: 'date',
                                            scaleType: 'band',
                                            tickSize: 8,
                                            height: 32,
                                            data: dateData,
                                            valueFormatter,
                                            tickLabelStyle: {
                                                fontWeight: 600,
                                            }
                                        }]}
                                        yAxis={[
                                            {
                                                id: 'leftAxis', reverse: false, width: 50, tickLabelStyle: {
                                                    fontWeight: 600,
                                                },

                                                valueFormatter: formatYAxisValue,
                                            },
                                            {
                                                id: 'rightAxis', reverse: true, position: 'right', width: 50, tickLabelStyle: {
                                                    fontWeight: 600,
                                                },
                                                valueFormatter: gbValueShow,
                                            },
                                        ]}

                                        dataset={combinedDataset}
                                        height={400}
                                    >
                                        <ChartsGrid horizontal />
                                        <BarPlot />
                                        <LinePlot />
                                        <MarkPlot />
                                        <ChartsXAxis />
                                        <ChartsYAxis axisId={"leftAxis"} label='User Counts' />
                                        <ChartsYAxis axisId={"rightAxis"} label='Data Usage (GB)' />
                                        <ChartsTooltip />
                                    </ChartContainer>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
                {/* </Grid> */}
            </Paper >

            {/* Expanded Chart Modal */}
            < Dialog
                open={expandedChart !== null
                }
                onClose={handleCloseExpanded}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        width: '95vw',
                        height: '90vh',
                        maxWidth: 'none',
                        m: 2
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" fontWeight={600}>
                        {expandedChart === 'barChart' && 'Data Usage By Carrier'}
                        {expandedChart === 'lineChart' && 'Rx - Tx By Carrier'}
                        {expandedChart === 'combinedChart' && 'Combined Data Usage'}
                    </Typography>
                    <IconButton onClick={handleCloseExpanded}>
                        <CloseIcon sx={{ color: "#005AA6" }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    <Box sx={{ width: '100%', height: '100%' }}>
                        {expandedChart === 'barChart' && (
                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                <BarChartLegend />
                                <BarChart
                                    xAxis={[{
                                        data: dateData,
                                        dataKey: 'date',
                                        scaleType: 'band',
                                        tickSize: 1,
                                        height: 32,
                                        valueFormatter,
                                        tickLabelStyle: { fontWeight: 600 }
                                    }]}
                                    yAxis={[{
                                        id: 'left',
                                        valueFormatter: formatYAxisValue,
                                        width: 80,
                                        tickLabelStyle: { fontWeight: 600 }
                                    }]}
                                    series={filteredBarSeries}
                                    dataset={stackedGroupedDataset}
                                    width={getChartWidth(true)}
                                    height={500}
                                    slots={{ legend: () => null }}
                                />
                            </Box>
                        )}
                        {expandedChart === 'lineChart' && (
                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                <LineChartLegend />
                                <LineChart
                                    xAxis={[{
                                        data: dateData,
                                        scaleType: 'band',
                                        tickSize: 1,
                                        height: 32,
                                        valueFormatter,
                                        tickLabelStyle: { fontWeight: 600 }
                                    }]}
                                    yAxis={[{
                                        id: 'left',
                                        valueFormatter: formatYAxisLineValue,
                                        width: 80,
                                        tickLabelStyle: { fontWeight: 600 }
                                    }]}
                                    series={getFilteredLineSeries()}
                                    width={getChartWidth(true)}
                                    height={500}
                                    slots={{ legend: () => null }}
                                />
                            </Box>
                        )}
                        {expandedChart === 'combinedChart' && (
                            <Stack width="100%">
                                <CombinedChartLegend />
                                <ChartContainer
                                    series={getFilteredCombinedSeries()}
                                    xAxis={[{
                                        dataKey: 'date',
                                        scaleType: 'band',
                                        tickSize: 8,
                                        height: 32,
                                        data: dateData,
                                        valueFormatter,
                                        tickLabelStyle: { fontWeight: 600 }
                                    }]}
                                    yAxis={[
                                        {
                                            id: 'leftAxis', reverse: false, width: 50,
                                            tickLabelStyle: { fontWeight: 600 },
                                            valueFormatter: formatYAxisValue,
                                        },
                                        {
                                            id: 'rightAxis', reverse: true, position: 'right', width: 50,
                                            tickLabelStyle: { fontWeight: 600 },
                                            valueFormatter: gbValueShow,
                                        },
                                    ]}
                                    dataset={combinedDataset}
                                    width={getChartWidth(true)}
                                    height={500}
                                >
                                    <ChartsGrid horizontal />
                                    <BarPlot />
                                    <LinePlot />
                                    <MarkPlot />
                                    <ChartsXAxis />
                                    <ChartsYAxis axisId={"leftAxis"} label='User Counts' />
                                    <ChartsYAxis axisId={"rightAxis"} label='Data Usage (GB)' />
                                    <ChartsTooltip />
                                </ChartContainer>
                            </Stack>
                        )}
                    </Box>
                </DialogContent>
            </Dialog >
        </>
    );
};

export default BarGraph;
